import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { formatMarathiDate, formatMarathiNumber, toMarathiDigits } from '../utils/marathi';
import { phoneHref } from '../utils/phone';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './StaffDirectory.css';

const CATEGORY_LABELS = {
  administrative: 'प्रशासकीय', technical: 'तांत्रिक', support: 'सहाय्यक',
  contracted: 'कंत्राटी', elected_member: 'लोकप्रतिनिधी', other: 'इतर',
};

const AVAILABILITY_LABELS = {
  available: 'कार्यालयात उपलब्ध', on_leave: 'रजेवर', retired: 'सेवानिवृत्त', transferred: 'बदली झाली',
};

function StaffIcon({ name, size = 20 }) {
  const paths = {
    search: <><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></>,
    phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.69 2.8a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.33 1.84.56 2.8.69A2 2 0 0 1 22 16.92z"/>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></>,
    building: <><path d="M4 22V3h12v19M2 22h20M8 7h4M8 11h4M8 15h4M16 10h4v12"/></>,
    education: <><path d="m2 10 10-5 10 5-10 5L2 10z"/><path d="M6 12v5c3 2 9 2 12 0v-5"/></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/></>,
    badge: <><circle cx="12" cy="8" r="5"/><path d="m8.5 12-2 9 5.5-3 5.5 3-2-9"/></>,
    pin: <><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0z"/><circle cx="12" cy="10" r="2.5"/></>,
    briefcase: <><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18M10 12v2h4v-2"/></>,
    user: <><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></>,
    arrow: <><path d="M5 12h14M13 6l6 6-6 6"/></>,
    close: <><path d="M6 6l12 12M18 6 6 18"/></>,
  };
  return <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>;
}

function StaffAvatar({ staff, className = '' }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [staff.photo_url]);
  const initials = (staff.full_name || '?').trim().split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase();
  if (!staff.photo_url || failed) {
    return <span className={`${className} staff-avatar-fallback`} aria-label={staff.full_name}>{initials}</span>;
  }
  return <img src={staff.photo_url} alt={staff.full_name} className={className} onError={() => setFailed(true)} />;
}

function DetailItem({ icon, label, children, wide = false }) {
  if (!children) return null;
  return <div className={`staff-detail-item${wide ? ' staff-detail-item-wide' : ''}`}>
    <span className="staff-detail-icon"><StaffIcon name={icon} /></span>
    <div><dt>{label}</dt><dd>{children}</dd></div>
  </div>;
}

function StaffDetailModal({ staff, onClose }) {
  useEffect(() => {
    const closeOnEscape = event => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', closeOnEscape);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const department = staff.Department?.name || staff.Department?.name_english;
  const designation = staff.designation_marathi || staff.designation;
  const availability = staff.availability || 'available';

  return <div className="staff-modal-overlay" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <section className="staff-profile-modal" role="dialog" aria-modal="true" aria-labelledby="staff-profile-title">
      <button type="button" className="staff-modal-close" onClick={onClose} aria-label="तपशील बंद करा"><StaffIcon name="close" /></button>

      <header className="staff-modal-identity">
        <div className="staff-modal-photo-shell"><StaffAvatar staff={staff} className="staff-modal-photo" /></div>
        <div className="staff-modal-title">
          <span className={`staff-availability staff-availability-${availability}`}><i />{AVAILABILITY_LABELS[availability] || availability}</span>
          <p>कर्मचारी परिचय</p>
          <h2 id="staff-profile-title">{staff.full_name}</h2>
          <strong>{designation}</strong>
          {department && <span className="staff-modal-department"><StaffIcon name="building" size={16} />{department}</span>}
        </div>
      </header>

      <div className="staff-modal-content">
        {(staff.phone || staff.email || staff.office_phone) && <div className="staff-contact-actions">
          {staff.phone && <a href={phoneHref(staff.phone)}><StaffIcon name="phone" /><span><small>मोबाइल</small>{staff.phone}</span></a>}
          {staff.office_phone && <a href={phoneHref(staff.office_phone)}><StaffIcon name="building" /><span><small>कार्यालयीन फोन</small>{staff.office_phone}</span></a>}
          {staff.email && <a href={`mailto:${staff.email}`}><StaffIcon name="mail" /><span><small>ईमेल</small>{staff.email}</span></a>}
        </div>}

        <div className="staff-detail-heading"><span>व्यावसायिक माहिती</span><i /></div>
        <dl className="staff-detail-grid">
          <DetailItem icon="briefcase" label="पदनाम">{designation}</DetailItem>
          <DetailItem icon="building" label="विभाग">{department}</DetailItem>
          <DetailItem icon="user" label="कर्मचारी प्रकार">{CATEGORY_LABELS[staff.category] || staff.category}</DetailItem>
          <DetailItem icon="calendar" label="अनुभव">{staff.experience_years !== null && staff.experience_years !== undefined ? `${toMarathiDigits(staff.experience_years)} वर्षे` : null}</DetailItem>
          <DetailItem icon="calendar" label="नियुक्ती दिनांक">{staff.appointment_date ? formatMarathiDate(staff.appointment_date) : null}</DetailItem>
          <DetailItem icon="pin" label="कार्यालयीन स्थान">{staff.office_location}</DetailItem>
          <DetailItem icon="education" label="शैक्षणिक पात्रता" wide>{staff.qualification}</DetailItem>
          <DetailItem icon="badge" label="विशेष कौशल्य" wide>{staff.specialization}</DetailItem>
          <DetailItem icon="badge" label="प्रमाणपत्रे" wide>{staff.certifications}</DetailItem>
          <DetailItem icon="pin" label="पत्ता" wide>{staff.address}</DetailItem>
        </dl>

        <footer className="staff-modal-footer">
          <p>नागरिकांच्या सोयीसाठी ग्रामपंचायतीने प्रकाशित केलेली अधिकृत कर्मचारी माहिती.</p>
          <button type="button" onClick={onClose}>बंद करा</button>
        </footer>
      </div>
    </section>
  </div>;
}

export default function StaffDirectory() {
  const { tenantSlug } = useParams();
  const [staffMembers, setStaffMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const [deptRes, staffRes] = await Promise.all([
          api.get(`/tenant/${tenantSlug}/departments`),
          api.get(`/tenant/${tenantSlug}/staff`),
        ]);
        setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
        setStaffMembers(Array.isArray(staffRes.data) ? staffRes.data : []);
      } catch (requestError) {
        console.error('Error fetching staff directory:', requestError);
        setError('कर्मचारी माहिती सध्या उपलब्ध नाही. कृपया थोड्या वेळाने पुन्हा प्रयत्न करा.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tenantSlug]);

  const departmentCounts = useMemo(() => staffMembers.reduce((counts, staff) => {
    counts[staff.department_id] = (counts[staff.department_id] || 0) + 1;
    return counts;
  }, {}), [staffMembers]);

  const filteredStaff = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('mr');
    return staffMembers.filter(staff => {
      const matchesDepartment = selectedDepartment === 'all' || String(staff.department_id) === selectedDepartment;
      const haystack = [staff.full_name, staff.designation_marathi, staff.designation, staff.Department?.name, staff.Department?.name_english, staff.specialization]
        .filter(Boolean).join(' ').toLocaleLowerCase('mr');
      return matchesDepartment && (!query || haystack.includes(query));
    });
  }, [search, selectedDepartment, staffMembers]);

  return <div className="staff-directory-page">
    <header className="staff-hero">
      <div className="staff-hero-copy">
        <span className="staff-hero-eyebrow">ग्रामपंचायत प्रशासन</span>
        <h1>कर्मचारी निर्देशिका</h1>
        <p>ग्रामपंचायतीतील अधिकारी व कर्मचाऱ्यांची अधिकृत संपर्क माहिती</p>
        <div className="staff-hero-metrics">
          <span><strong>{formatMarathiNumber(staffMembers.length)}</strong> कर्मचारी</span>
          <i />
          <span><strong>{formatMarathiNumber(departments.length)}</strong> विभाग</span>
        </div>
      </div>
    </header>

    <section className="staff-directory-tools" aria-label="कर्मचारी शोध आणि विभाग निवड">
      <div className="staff-search-box">
        <StaffIcon name="search" />
        <input value={search} onChange={event => setSearch(event.target.value)} placeholder="नाव, पद किंवा विभाग शोधा" aria-label="कर्मचारी शोधा" />
        {search && <button type="button" onClick={() => setSearch('')} aria-label="शोध साफ करा"><StaffIcon name="close" size={17} /></button>}
      </div>
      <div className="staff-filter-pills" role="group" aria-label="विभागानुसार कर्मचारी">
        <button type="button" className={`staff-pill ${selectedDepartment === 'all' ? 'active' : ''}`} onClick={() => setSelectedDepartment('all')}>
          सर्व विभाग <span>{formatMarathiNumber(staffMembers.length)}</span>
        </button>
        {departments.filter(dept => departmentCounts[dept.id]).map(dept => <button
          type="button" key={dept.id} className={`staff-pill ${selectedDepartment === String(dept.id) ? 'active' : ''}`}
          onClick={() => setSelectedDepartment(String(dept.id))}
        >{dept.name || dept.name_english}<span>{formatMarathiNumber(departmentCounts[dept.id])}</span></button>)}
      </div>
    </section>

    <section className="staff-grid-wrap" aria-live="polite">
      <div className="staff-results-heading">
        <div><span>कर्मचारी सूची</span><h2>{selectedDepartment === 'all' ? 'सर्व कर्मचारी' : departments.find(dept => String(dept.id) === selectedDepartment)?.name || 'विभागातील कर्मचारी'}</h2></div>
        {!loading && !error && <p>{formatMarathiNumber(filteredStaff.length)} नोंदी आढळल्या</p>}
      </div>

      {loading ? <LoadingSpinner fullPage message="कर्मचारी माहिती लोड होत आहे…" />
        : error ? <div className="staff-state staff-state-error"><StaffIcon name="user" size={30} /><strong>{error}</strong></div>
        : filteredStaff.length === 0 ? <div className="staff-state"><StaffIcon name="search" size={30} /><strong>या शोधासाठी कर्मचारी सापडले नाहीत</strong><button type="button" onClick={() => { setSearch(''); setSelectedDepartment('all'); }}>सर्व कर्मचारी पहा</button></div>
        : <div className="staff-grid">
          {filteredStaff.map(staff => {
            const department = staff.Department?.name || staff.Department?.name_english;
            const availability = staff.availability || 'available';
            return <article key={staff.id} className="staff-profile-card">
              <button type="button" className="staff-card-main" onClick={() => setSelectedStaff(staff)} aria-label={`${staff.full_name} यांचा तपशील पहा`}>
                <div className="staff-card-photo-shell"><StaffAvatar staff={staff} className="staff-card-photo" /><span className={`staff-card-status staff-card-status-${availability}`} title={AVAILABILITY_LABELS[availability]} /></div>
                <div className="staff-card-copy">
                  {department && <span className="staff-card-department">{department}</span>}
                  <h3>{staff.full_name}</h3>
                  <p>{staff.designation_marathi || staff.designation}</p>
                </div>
                <span className="staff-card-arrow"><StaffIcon name="arrow" /></span>
              </button>
              <div className="staff-card-footer">
                <span><i className={`availability-dot availability-${availability}`} />{AVAILABILITY_LABELS[availability] || availability}</span>
                <div>
                  {staff.phone && <a href={phoneHref(staff.phone)} aria-label={`${staff.full_name} यांना फोन करा`}><StaffIcon name="phone" size={17} /></a>}
                  {staff.email && <a href={`mailto:${staff.email}`} aria-label={`${staff.full_name} यांना ईमेल करा`}><StaffIcon name="mail" size={17} /></a>}
                  <button type="button" onClick={() => setSelectedStaff(staff)}>तपशील</button>
                </div>
              </div>
            </article>;
          })}
        </div>}
    </section>

    {selectedStaff && <StaffDetailModal staff={selectedStaff} onClose={() => setSelectedStaff(null)} />}
  </div>;
}
